/**
 * Rasterize text (plain or rich runs) into ImageData for Image Editor.
 * Phase 2a: runs[] + layout cache; wrap-to-box deferred.
 */

/** Reasonable web-safe set for v1. */
export const IMAGE_EDITOR_TEXT_FONTS = [
  'Arial, Helvetica, sans-serif',
  'Georgia, serif',
  '"Times New Roman", Times, serif',
  '"Courier New", Courier, monospace',
  'Verdana, Geneva, sans-serif',
  'Impact, Charcoal, sans-serif',
];

const DEFAULT_FONT_FAMILY = IMAGE_EDITOR_TEXT_FONTS[0];
const TEXT_PAD = 4;

/**
 * Extra trailing width so a contentEditable caret at end-of-line does not
 * force a CSS soft-wrap (browsers need room beyond the last glyph).
 * @param {number} fontSize
 * @returns {number}
 */
export function textLayoutCaretSlackPx(fontSize) {
  const size = Number(fontSize);
  if (!Number.isFinite(size) || size <= 0) return 2;
  return Math.max(2, Math.ceil(size * 0.25));
}

/**
 * @typedef {{
 *   text: string,
 *   bold?: boolean,
 *   italic?: boolean,
 *   color?: string,
 *   fontFamily?: string,
 *   fontSize?: number,
 * }} EditorTextRun
 */

/**
 * @typedef {{
 *   text: string,
 *   fontFamily: string,
 *   fontSize: number,
 *   color: string,
 *   align: 'left'|'center'|'right',
 *   bold?: boolean,
 *   italic?: boolean,
 *   runs?: EditorTextRun[],
 * }} EditorTextParams
 */

/**
 * @typedef {{
 *   runIndex: number,
 *   text: string,
 *   x: number,
 *   y: number,
 *   width: number,
 *   height: number,
 *   font: string,
 *   color: string,
 *   bold: boolean,
 *   italic: boolean,
 *   fontSize: number,
 *   fontFamily: string,
 *   charStart: number,
 *   charEnd: number,
 * }} TextLayoutSegment
 */

/**
 * @typedef {{
 *   index: number,
 *   y: number,
 *   height: number,
 *   width: number,
 *   x0: number,
 *   segments: TextLayoutSegment[],
 * }} TextLayoutLine
 */

/**
 * Reusable layout cache for Phase 2b hit-testing / caret.
 * @typedef {{
 *   pad: number,
 *   align: 'left'|'center'|'right',
 *   width: number,
 *   height: number,
 *   defaults: {
 *     fontFamily: string,
 *     fontSize: number,
 *     color: string,
 *     bold: boolean,
 *     italic: boolean,
 *   },
 *   runs: Array<{
 *     text: string,
 *     bold: boolean,
 *     italic: boolean,
 *     color: string,
 *     fontFamily: string,
 *     fontSize: number,
 *   }>,
 *   lines: TextLayoutLine[],
 *   flatText: string,
 * }} TextLayoutCache
 */

/**
 * @param {unknown} value
 * @param {string} fallback
 * @returns {string}
 */
function normalizeFontFamily(value, fallback = DEFAULT_FONT_FAMILY) {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

/**
 * @param {unknown} value
 * @param {number} fallback
 * @returns {number}
 */
function normalizeFontSize(value, fallback = 32) {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return fallback;
  return Math.max(8, Math.min(256, n));
}

/**
 * @param {unknown} value
 * @param {string} fallback
 * @returns {string}
 */
function normalizeColor(value, fallback = '#000000') {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

/**
 * @param {unknown} value
 * @returns {'left'|'center'|'right'}
 */
function normalizeAlign(value) {
  return value === 'center' || value === 'right' ? value : 'left';
}

/**
 * @param {string} text
 * @returns {string}
 */
function normalizeNewlines(text) {
  return String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
}

/**
 * @param {{
 *   fontFamily: string,
 *   fontSize: number,
 *   bold?: boolean,
 *   italic?: boolean,
 * }} style
 * @returns {string}
 */
export function buildTextFontCss(style) {
  const italic = style.italic ? 'italic ' : '';
  const bold = style.bold ? 'bold ' : '';
  return `${italic}${bold}${style.fontSize}px ${style.fontFamily}`;
}

/**
 * Normalize flat text params (block defaults). Does not invent runs.
 * @param {Partial<EditorTextParams>|null|undefined} params
 * @returns {Omit<EditorTextParams, 'runs'>}
 */
export function normalizeEditorTextParams(params) {
  const src = params && typeof params === 'object' ? params : {};
  return {
    text: normalizeNewlines(src.text == null ? '' : String(src.text)),
    fontFamily: normalizeFontFamily(src.fontFamily),
    fontSize: normalizeFontSize(src.fontSize),
    color: normalizeColor(src.color),
    align: normalizeAlign(src.align),
    bold: Boolean(src.bold),
    italic: Boolean(src.italic),
  };
}

/**
 * @param {unknown} run
 * @param {Omit<EditorTextParams, 'runs'>} defaults
 * @returns {{
 *   text: string,
 *   bold: boolean,
 *   italic: boolean,
 *   color: string,
 *   fontFamily: string,
 *   fontSize: number,
 * }}
 */
export function normalizeTextRun(run, defaults) {
  const src = run && typeof run === 'object' ? run : {};
  return {
    text: normalizeNewlines(src.text == null ? '' : String(src.text)),
    bold: src.bold !== undefined ? Boolean(src.bold) : Boolean(defaults.bold),
    italic:
      src.italic !== undefined ? Boolean(src.italic) : Boolean(defaults.italic),
    color:
      src.color !== undefined
        ? normalizeColor(src.color, defaults.color)
        : defaults.color,
    fontFamily:
      src.fontFamily !== undefined
        ? normalizeFontFamily(src.fontFamily, defaults.fontFamily)
        : defaults.fontFamily,
    fontSize:
      src.fontSize !== undefined
        ? normalizeFontSize(src.fontSize, defaults.fontSize)
        : defaults.fontSize,
  };
}

/**
 * v2 flat params → one synthetic run carrying the exact values.
 * @param {Partial<EditorTextParams>|null|undefined} params
 * @returns {ReturnType<typeof normalizeTextRun>[]}
 */
export function synthesizeRunsFromFlatParams(params) {
  const defaults = normalizeEditorTextParams(params);
  return [
    normalizeTextRun(
      {
        text: defaults.text,
        bold: defaults.bold,
        italic: defaults.italic,
        color: defaults.color,
        fontFamily: defaults.fontFamily,
        fontSize: defaults.fontSize,
      },
      defaults,
    ),
  ];
}

/**
 * @param {Array<Partial<EditorTextRun>|null|undefined>|null|undefined} runs
 * @param {Omit<EditorTextParams, 'runs'>} defaults
 * @returns {ReturnType<typeof normalizeTextRun>[]}
 */
export function normalizeTextRuns(runs, defaults) {
  if (!Array.isArray(runs) || runs.length === 0) {
    return synthesizeRunsFromFlatParams(defaults);
  }
  return runs.map((run) => normalizeTextRun(run, defaults));
}

/**
 * @param {Array<{ text: string }>} runs
 * @returns {string}
 */
export function flattenRunsToText(runs) {
  return (runs || []).map((r) => String(r?.text || '')).join('');
}

/**
 * Deep-clone run list (survives duplicate/clone — do not strip).
 * @param {EditorTextRun[]|null|undefined} runs
 * @returns {EditorTextRun[]}
 */
export function cloneTextRuns(runs) {
  if (!Array.isArray(runs)) return [];
  return runs.map((run) => {
    const src = run && typeof run === 'object' ? run : {};
    /** @type {EditorTextRun} */
    const out = { text: String(src.text || '') };
    if (src.bold !== undefined) out.bold = Boolean(src.bold);
    if (src.italic !== undefined) out.italic = Boolean(src.italic);
    if (src.color !== undefined) out.color = String(src.color);
    if (src.fontFamily !== undefined) out.fontFamily = String(src.fontFamily);
    if (src.fontSize !== undefined) {
      out.fontSize = normalizeFontSize(src.fontSize);
    }
    return out;
  });
}

/**
 * Resolve params + runs for render/persist. Accepts v2 flat or v3 with runs.
 * @param {Partial<EditorTextParams>|null|undefined} params
 * @returns {{
 *   params: Omit<EditorTextParams, 'runs'> & { runs: ReturnType<typeof normalizeTextRun>[] },
 *   runs: ReturnType<typeof normalizeTextRun>[],
 * }}
 */
export function resolveEditorTextContent(params) {
  const base = normalizeEditorTextParams(params);
  const rawRuns =
    params && typeof params === 'object' && Array.isArray(params.runs)
      ? params.runs
      : null;
  const runs = normalizeTextRuns(rawRuns, base);
  const flatText = flattenRunsToText(runs);
  const nextParams = {
    ...base,
    text: flatText || base.text,
    runs: runs.map((r) => ({ ...r })),
  };
  return { params: nextParams, runs: nextParams.runs };
}

/**
 * Apply whole-object style/content patch onto runs (Phase 2a options bar).
 * @param {ReturnType<typeof normalizeTextRun>[]} runs
 * @param {Omit<EditorTextParams, 'runs'>} defaults
 * @param {Partial<EditorTextParams>} patch
 * @returns {ReturnType<typeof normalizeTextRun>[]}
 */
export function applyWholeObjectPatchToRuns(runs, defaults, patch) {
  if (patch.runs !== undefined) {
    return normalizeTextRuns(patch.runs, defaults);
  }
  if (patch.text !== undefined) {
    // Content field is whole-object in 2a — collapse to one styled run.
    return [
      normalizeTextRun(
        {
          text: String(patch.text),
          bold: defaults.bold,
          italic: defaults.italic,
          color: defaults.color,
          fontFamily: defaults.fontFamily,
          fontSize: defaults.fontSize,
        },
        defaults,
      ),
    ];
  }
  const next = (runs.length ? runs : synthesizeRunsFromFlatParams(defaults)).map(
    (run) => ({ ...run }),
  );
  for (const run of next) {
    if (patch.fontFamily !== undefined) {
      run.fontFamily = normalizeFontFamily(patch.fontFamily, defaults.fontFamily);
    }
    if (patch.fontSize !== undefined) {
      run.fontSize = normalizeFontSize(patch.fontSize, defaults.fontSize);
    }
    if (patch.color !== undefined) {
      run.color = normalizeColor(patch.color, defaults.color);
    }
    if (patch.bold !== undefined) run.bold = Boolean(patch.bold);
    if (patch.italic !== undefined) run.italic = Boolean(patch.italic);
  }
  return next;
}

/**
 * True when two normalized runs share the same style (ignore text).
 * @param {{ bold?: boolean, italic?: boolean, color?: string, fontFamily?: string, fontSize?: number }} a
 * @param {{ bold?: boolean, italic?: boolean, color?: string, fontFamily?: string, fontSize?: number }} b
 * @returns {boolean}
 */
export function textRunStylesEqual(a, b) {
  if (!a || !b) return false;
  return (
    Boolean(a.bold) === Boolean(b.bold) &&
    Boolean(a.italic) === Boolean(b.italic) &&
    String(a.color || '') === String(b.color || '') &&
    String(a.fontFamily || '') === String(b.fontFamily || '') &&
    Number(a.fontSize) === Number(b.fontSize)
  );
}

/**
 * Merge adjacent runs that share identical style (Phase 2b Slice B).
 * @param {EditorTextRun[]|null|undefined} runs
 * @param {Partial<EditorTextParams>|null|undefined} [defaults]
 * @returns {ReturnType<typeof normalizeTextRun>[]}
 */
export function coalesceAdjacentTextRuns(runs, defaults) {
  const base = normalizeEditorTextParams(defaults || {});
  const normalized = normalizeTextRuns(runs, base);
  /** @type {ReturnType<typeof normalizeTextRun>[]} */
  const out = [];
  for (const run of normalized) {
    if (!run.text) continue;
    const last = out[out.length - 1];
    if (last && textRunStylesEqual(last, run)) {
      last.text += run.text;
    } else {
      out.push({ ...run });
    }
  }
  if (!out.length) {
    return synthesizeRunsFromFlatParams({ ...base, text: '' });
  }
  return out;
}

/**
 * Apply bold/italic/color to a character-offset range within runs[].
 * Splits at boundaries and coalesces adjacent identical styles.
 *
 * @param {EditorTextRun[]|null|undefined} runs
 * @param {number} startOffset
 * @param {number} endOffset
 * @param {{ bold?: boolean, italic?: boolean, color?: string }} stylePatch
 * @param {Partial<EditorTextParams>|null|undefined} [defaults]
 * @returns {ReturnType<typeof normalizeTextRun>[]}
 */
export function applyStyleToTextRange(
  runs,
  startOffset,
  endOffset,
  stylePatch,
  defaults,
) {
  const base = normalizeEditorTextParams(defaults || {});
  const normalized = normalizeTextRuns(runs, base);
  const flat = flattenRunsToText(normalized);
  let start = Math.max(0, Math.min(Number(startOffset) || 0, Number(endOffset) || 0));
  let end = Math.max(0, Math.max(Number(startOffset) || 0, Number(endOffset) || 0));
  start = Math.min(start, flat.length);
  end = Math.min(end, flat.length);
  if (start === end || !stylePatch || typeof stylePatch !== 'object') {
    return coalesceAdjacentTextRuns(normalized, base);
  }

  /** @type {ReturnType<typeof normalizeTextRun>[]} */
  const pieces = [];
  let cursor = 0;
  for (const run of normalized) {
    const runStart = cursor;
    const runEnd = cursor + run.text.length;
    cursor = runEnd;
    if (runEnd <= start || runStart >= end) {
      pieces.push({ ...run });
      continue;
    }
    if (runStart < start) {
      pieces.push({
        ...run,
        text: run.text.slice(0, start - runStart),
      });
    }
    const midFrom = Math.max(runStart, start) - runStart;
    const midTo = Math.min(runEnd, end) - runStart;
    const styled = { ...run };
    if (stylePatch.bold !== undefined) styled.bold = Boolean(stylePatch.bold);
    if (stylePatch.italic !== undefined) {
      styled.italic = Boolean(stylePatch.italic);
    }
    if (stylePatch.color !== undefined) {
      styled.color = normalizeColor(stylePatch.color, run.color);
    }
    pieces.push({
      ...styled,
      text: run.text.slice(midFrom, midTo),
    });
    if (runEnd > end) {
      pieces.push({
        ...run,
        text: run.text.slice(end - runStart),
      });
    }
  }
  return coalesceAdjacentTextRuns(pieces, base);
}

/**
 * Insert plain text at a character-offset range (paste / typing-equivalent).
 * Replaces [startOffset, endOffset) and inherits style from the run at the
 * insertion point. Coalesces adjacent identical styles.
 *
 * @param {EditorTextRun[]|null|undefined} runs
 * @param {number} startOffset
 * @param {number} endOffset
 * @param {string} plainText
 * @param {Partial<EditorTextParams>|null|undefined} [defaults]
 * @returns {ReturnType<typeof normalizeTextRun>[]}
 */
export function insertPlainTextIntoRuns(
  runs,
  startOffset,
  endOffset,
  plainText,
  defaults,
) {
  const base = normalizeEditorTextParams(defaults || {});
  const normalized = normalizeTextRuns(runs, base);
  const flat = flattenRunsToText(normalized);
  let start = Math.max(0, Math.min(Number(startOffset) || 0, Number(endOffset) || 0));
  let end = Math.max(0, Math.max(Number(startOffset) || 0, Number(endOffset) || 0));
  start = Math.min(start, flat.length);
  end = Math.min(end, flat.length);
  const plain = normalizeNewlines(String(plainText || '')).replace(/\u00a0/g, ' ');

  /** @type {ReturnType<typeof normalizeTextRun>} */
  let inherit = normalizeTextRun(
    {
      text: '',
      bold: base.bold,
      italic: base.italic,
      color: base.color,
      fontFamily: base.fontFamily,
      fontSize: base.fontSize,
    },
    base,
  );
  let cursor = 0;
  for (const run of normalized) {
    const runEnd = cursor + run.text.length;
    if (start >= cursor && start <= runEnd) {
      inherit = { ...run, text: '' };
      break;
    }
    cursor = runEnd;
  }

  /** @type {ReturnType<typeof normalizeTextRun>[]} */
  const out = [];
  cursor = 0;
  let didInsert = false;
  for (const run of normalized) {
    const runStart = cursor;
    const runEnd = cursor + run.text.length;
    cursor = runEnd;

    if (runEnd <= start) {
      out.push({ ...run });
      continue;
    }
    if (runStart >= end) {
      if (!didInsert) {
        if (plain) out.push({ ...inherit, text: plain });
        didInsert = true;
      }
      out.push({ ...run });
      continue;
    }

    // Overlaps [start, end)
    if (runStart < start) {
      out.push({ ...run, text: run.text.slice(0, start - runStart) });
    }
    if (!didInsert) {
      if (plain) out.push({ ...inherit, text: plain });
      didInsert = true;
    }
    if (runEnd > end) {
      out.push({ ...run, text: run.text.slice(end - runStart) });
    }
  }
  if (!didInsert) {
    if (plain) out.push({ ...inherit, text: plain });
  }
  return coalesceAdjacentTextRuns(out, base);
}

/**
 * Summarize bold/italic/color across a character range (for toolbar state).
 * @param {EditorTextRun[]|null|undefined} runs
 * @param {number} startOffset
 * @param {number} endOffset
 * @param {Partial<EditorTextParams>|null|undefined} [defaults]
 * @returns {{ bold: boolean|null, italic: boolean|null, color: string|null }}
 */
export function summarizeStyleInTextRange(
  runs,
  startOffset,
  endOffset,
  defaults,
) {
  const base = normalizeEditorTextParams(defaults || {});
  const normalized = normalizeTextRuns(runs, base);
  const flat = flattenRunsToText(normalized);
  let start = Math.max(0, Math.min(Number(startOffset) || 0, Number(endOffset) || 0));
  let end = Math.max(0, Math.max(Number(startOffset) || 0, Number(endOffset) || 0));
  start = Math.min(start, flat.length);
  end = Math.min(end, flat.length);
  if (start === end) {
    return {
      bold: Boolean(base.bold),
      italic: Boolean(base.italic),
      color: base.color,
    };
  }
  /** @type {boolean|null} */
  let bold = null;
  /** @type {boolean|null} */
  let italic = null;
  /** @type {string|null} */
  let color = null;
  let cursor = 0;
  for (const run of normalized) {
    const runStart = cursor;
    const runEnd = cursor + run.text.length;
    cursor = runEnd;
    if (runEnd <= start || runStart >= end || !run.text) continue;
    if (bold === null) bold = Boolean(run.bold);
    else if (bold !== Boolean(run.bold)) bold = null;
    if (italic === null) italic = Boolean(run.italic);
    else if (italic !== Boolean(run.italic)) italic = null;
    if (color === null) color = String(run.color || base.color);
    else if (color !== String(run.color || base.color)) color = null;
  }
  return {
    bold: bold === null && start < end ? null : Boolean(bold),
    italic: italic === null && start < end ? null : Boolean(italic),
    color,
  };
}

/**
 * Build layout cache (measure + positions). Does not draw.
 * Empty / whitespace-only text still returns a minimum placeholder box so the
 * float stays clickable and re-edit can show a caret.
 *
 * @param {Partial<EditorTextParams>} params
 * @param {CanvasRenderingContext2D} ctx
 * @returns {TextLayoutCache|null}
 */
export function layoutTextContent(params, ctx) {
  const { params: resolved, runs } = resolveEditorTextContent(params);
  const flatText = resolved.text;
  const defaults = {
    fontFamily: resolved.fontFamily,
    fontSize: resolved.fontSize,
    color: resolved.color,
    bold: Boolean(resolved.bold),
    italic: Boolean(resolved.italic),
  };
  const align = resolved.align;

  // Empty placeholder: one em-ish wide line so hit-test / edit surface survive.
  if (!flatText.trim()) {
    const font = buildTextFontCss(defaults);
    ctx.font = font;
    const minW = Math.max(
      ctx.measureText('M').width,
      Math.ceil(defaults.fontSize * 0.75),
    );
    const lineHeight = Math.ceil(defaults.fontSize * 1.25);
    const width = Math.max(
      1,
      Math.ceil(minW) + TEXT_PAD * 2 + textLayoutCaretSlackPx(defaults.fontSize),
    );
    const height = Math.max(1, lineHeight + TEXT_PAD * 2);
    const placeholderRuns =
      runs.length > 0
        ? runs.map((r) => ({ ...r, text: r.text || '' }))
        : [
            {
              text: '',
              bold: defaults.bold,
              italic: defaults.italic,
              color: defaults.color,
              fontFamily: defaults.fontFamily,
              fontSize: defaults.fontSize,
            },
          ];
    return {
      pad: TEXT_PAD,
      align,
      width,
      height,
      defaults,
      runs: placeholderRuns,
      lines: [
        {
          index: 0,
          y: TEXT_PAD,
          height: lineHeight,
          width: 0,
          x0: TEXT_PAD,
          segments: [],
        },
      ],
      flatText: '',
    };
  }

  /** @type {Array<Array<{ runIndex: number, text: string, style: ReturnType<typeof normalizeTextRun>, charStart: number }>>} */
  const lineBuilders = [[]];
  let globalChar = 0;

  runs.forEach((run, runIndex) => {
    const parts = run.text.split('\n');
    parts.forEach((part, partIndex) => {
      if (partIndex > 0) lineBuilders.push([]);
      const line = lineBuilders[lineBuilders.length - 1];
      line.push({ runIndex, text: part, style: run, charStart: globalChar });
      globalChar += part.length;
      if (partIndex < parts.length - 1) {
        // newline consumed between parts
        globalChar += 1;
      }
    });
  });

  /** @type {TextLayoutLine[]} */
  const lines = [];
  let maxLineW = 0;
  let totalHeight = 0;

  lineBuilders.forEach((segs, lineIndex) => {
    /** @type {TextLayoutSegment[]} */
    const measured = [];
    let lineW = 0;
    let maxSize = defaults.fontSize;
    for (const seg of segs) {
      const font = buildTextFontCss(seg.style);
      ctx.font = font;
      const raw = seg.text;
      const measureStr = raw.length ? raw : ' ';
      const width = ctx.measureText(measureStr).width;
      const height = Math.ceil(seg.style.fontSize * 1.25);
      maxSize = Math.max(maxSize, seg.style.fontSize);
      const drawWidth = raw.length ? width : 0;
      measured.push({
        runIndex: seg.runIndex,
        text: raw,
        x: 0,
        y: 0,
        width: drawWidth,
        height,
        font,
        color: seg.style.color,
        bold: seg.style.bold,
        italic: seg.style.italic,
        fontSize: seg.style.fontSize,
        fontFamily: seg.style.fontFamily,
        charStart: seg.charStart,
        charEnd: seg.charStart + raw.length,
      });
      lineW += drawWidth;
    }
    if (!measured.length) {
      ctx.font = buildTextFontCss(defaults);
      const height = Math.ceil(defaults.fontSize * 1.25);
      maxLineW = Math.max(maxLineW, ctx.measureText(' ').width);
      lines.push({
        index: lineIndex,
        y: 0,
        height,
        width: 0,
        x0: 0,
        segments: [],
      });
      totalHeight += height;
      return;
    }
    const lineHeight = Math.ceil(maxSize * 1.25);
    maxLineW = Math.max(maxLineW, lineW || ctx.measureText(' ').width);
    lines.push({
      index: lineIndex,
      y: 0,
      height: lineHeight,
      width: lineW,
      x0: 0,
      segments: measured,
    });
    totalHeight += lineHeight;
  });

  const width = Math.max(
    1,
    Math.ceil(maxLineW) + TEXT_PAD * 2 + textLayoutCaretSlackPx(defaults.fontSize),
  );
  const height = Math.max(1, totalHeight + TEXT_PAD * 2);

  let y = TEXT_PAD;
  for (const line of lines) {
    line.y = y;
    const x0 =
      align === 'center'
        ? (width - line.width) / 2
        : align === 'right'
          ? width - TEXT_PAD - line.width
          : TEXT_PAD;
    line.x0 = x0;
    let x = x0;
    for (const seg of line.segments) {
      seg.x = x;
      seg.y = y;
      seg.height = line.height;
      x += seg.width;
    }
    y += line.height;
  }

  return {
    pad: TEXT_PAD,
    align,
    width,
    height,
    defaults,
    runs: runs.map((r) => ({ ...r })),
    lines,
    flatText,
  };
}

/**
 * Draw a previously built layout into ImageData.
 * @param {TextLayoutCache} layout
 * @returns {ImageData|null}
 */
export function renderTextLayoutToImageData(layout) {
  if (!layout || typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = layout.width;
  canvas.height = layout.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.clearRect(0, 0, layout.width, layout.height);
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  for (const line of layout.lines) {
    for (const seg of line.segments) {
      if (!seg.text) continue;
      ctx.font = seg.font;
      ctx.fillStyle = seg.color;
      ctx.fillText(seg.text, seg.x, seg.y);
    }
  }
  return ctx.getImageData(0, 0, layout.width, layout.height);
}

/**
 * Layout + rasterize. Returns ImageData only (legacy callers).
 * @param {Partial<EditorTextParams>} params
 * @returns {ImageData|null}
 */
export function renderTextToImageData(params) {
  const withLayout = renderTextToImageDataWithLayout(params);
  return withLayout ? withLayout.imageData : null;
}

/**
 * Layout + rasterize with reusable layout cache (Phase 2b).
 * Empty text yields a minimum placeholder ImageData (not null).
 * @param {Partial<EditorTextParams>} params
 * @returns {{ imageData: ImageData, layout: TextLayoutCache }|null}
 */
export function renderTextToImageDataWithLayout(params) {
  if (typeof document === 'undefined') return null;
  const { params: resolved } = resolveEditorTextContent(params);

  const measureCanvas = document.createElement('canvas');
  const measureCtx = measureCanvas.getContext('2d');
  if (!measureCtx) return null;
  const layout = layoutTextContent(resolved, measureCtx);
  if (!layout) return null;
  const imageData = renderTextLayoutToImageData(layout);
  if (!imageData) return null;
  return { imageData, layout };
}

/**
 * Short label for layer naming from text content.
 * @param {string} text
 * @returns {string}
 */
export function textLayerPreviewLabel(text) {
  const one = String(text || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!one) return 'Text';
  return one.slice(0, 28);
}

/**
 * Re-rasterize an active text float after content/style edits.
 * Preserves position/scale/rotation. Empty text keeps prior pixels but
 * updates textParams so the user can keep typing.
 *
 * @param {import('./imageEditorFloat').EditorFloat} float
 * @param {Partial<EditorTextParams>} patch
 * @param {(params: Partial<EditorTextParams>) => ImageData|null} renderFn
 * @returns {import('./imageEditorFloat').EditorFloat|null}
 */
export function applyTextFloatContentPatch(float, patch, renderFn) {
  if (!float || float.origin !== 'text') return null;
  const prevResolved = resolveEditorTextContent(
    float.textParams || {
      text: float.labelHint || 'Text',
      fontFamily: DEFAULT_FONT_FAMILY,
      fontSize: 32,
      color: '#000000',
      align: /** @type {'left'} */ ('left'),
    },
  );
  const baseDefaults = {
    text: prevResolved.params.text,
    fontFamily: prevResolved.params.fontFamily,
    fontSize: prevResolved.params.fontSize,
    color: prevResolved.params.color,
    align: prevResolved.params.align,
    bold: Boolean(prevResolved.params.bold),
    italic: Boolean(prevResolved.params.italic),
  };
  const nextDefaults = {
    ...baseDefaults,
    text:
      patch.text !== undefined ? normalizeNewlines(String(patch.text)) : baseDefaults.text,
    fontFamily:
      patch.fontFamily !== undefined
        ? normalizeFontFamily(patch.fontFamily)
        : baseDefaults.fontFamily,
    fontSize:
      patch.fontSize !== undefined
        ? normalizeFontSize(patch.fontSize)
        : baseDefaults.fontSize,
    color:
      patch.color !== undefined
        ? normalizeColor(patch.color)
        : baseDefaults.color,
    align:
      patch.align === 'center' ||
      patch.align === 'right' ||
      patch.align === 'left'
        ? patch.align
        : baseDefaults.align,
    bold: patch.bold !== undefined ? Boolean(patch.bold) : baseDefaults.bold,
    italic:
      patch.italic !== undefined ? Boolean(patch.italic) : baseDefaults.italic,
  };
  const nextRuns = applyWholeObjectPatchToRuns(
    prevResolved.runs,
    nextDefaults,
    patch,
  );
  const nextParams = {
    ...nextDefaults,
    text: flattenRunsToText(nextRuns),
    runs: cloneTextRuns(nextRuns),
  };
  // Prefer { imageData, layout } so callers can reuse TextLayoutCache (Slice C)
  // instead of rebuilding layoutTextContent for caret/format mapping.
  const renderedRaw = renderFn(nextParams);
  let rendered = null;
  let layout = null;
  if (renderedRaw && typeof renderedRaw === 'object' && renderedRaw.imageData) {
    rendered = renderedRaw.imageData;
    layout = renderedRaw.layout || null;
  } else {
    rendered = renderedRaw;
  }
  const labelHint = textLayerPreviewLabel(nextParams.text);
  if (!rendered) {
    return {
      ...float,
      textParams: nextParams,
      labelHint: labelHint || float.labelHint,
      textLayout: layout || float.textLayout || null,
    };
  }
  return {
    ...float,
    imageData: rendered,
    width: rendered.width,
    height: rendered.height,
    textParams: nextParams,
    labelHint: labelHint || 'Text',
    textLayout: layout || null,
  };
}
